import {
  Button,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles
} from '@material-ui/core/styles';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSnackbar } from 'notistack';
import {
  getLedgerList,
  copyLedger,
  downloadLedger,
  deleteLedger
} from '../apicalls/ledger';
import '../App.css';
import AppBarComponent from '../components/parts/appBar/AppBarComponent';
import {
  checkResData,
  formatDatetime,
  getConstructionsSelectBoxData
} from '../utils/common';
import FooterComponent from '../components/FooterComponent';
import LoadingComponent from '../components/LoadingComponent';
import PaginationComponent from '../components/PaginationComponent';
import {
  ledgerListState,
  isLoggedIn,
  mainBlueColor,
  userMenuId,
  userTenant,
  progressFlgState,
  selectedPicturesState
} from '../GlobalState';
import './List.css';
import { TSelectItem } from '../types/common';
import { TLedger, TSelect } from '../types/Ledger';
import { LedgerPdf } from '../components/ledger/LedgerPdf';
import { TTenant } from '../types/common';
import { useForm, UseFormReturn, FormProvider } from 'react-hook-form';
import { AppSelectfield } from '../components/parts/input/AppSelectfield';
import { TPicture } from '../types/cloudStorage/CloudFileList';
import { List } from '../components/ledgerList/List';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  body: {
    fontSize: 16
  }
}))(TableCell);

// 初期データ
const defaultValues: TSelect = {
  construct_id: { value: 0, label: '案件名' }
};

export const LedgerListPage: React.FC = () => {
  const [mainColor] = useRecoilState(mainBlueColor);
  const userTenantData = useRecoilValue<TTenant | undefined>(userTenant);
  const [, setProgressFlg] = useRecoilState<boolean>(progressFlgState);
  // リクエスト結果のメッセージ
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [loggedIn, setLoggedIn] = useRecoilState(isLoggedIn);
  const changeLoggedInData = useCallback(
    (newData: string) => setLoggedIn(newData),
    [setLoggedIn]
  );
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [constructList, setConstructList] = useState<TSelectItem[]>([]);
  const [, setImageData] = useRecoilState<TPicture[]>(selectedPicturesState);

  const [ledgerList, setLedgerList] = useRecoilState(ledgerListState);
  const [loading, setLoading] = useState(ledgerList !== null ? false : true);

  // hook-formの初期設定
  const methods: UseFormReturn<TSelect, any> = useForm({ defaultValues });
  const {
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  function Alert(props: AlertProps) {
    return <MuiAlert elevation={6} variant='filled' {...props} />;
  }

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const classes = useStyles({ mainColor: mainColor });

  // サイズ調整
  const theme = useTheme();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    setImageData([]);
    // 工事セレクトボックスを取得
    getConstructionsSelectBoxData(setConstructList, enqueueSnackbar, {
      value: 0,
      label: '--'
    });
  }, []);

  useEffect(() => {
    getList();
  }, [getValues('construct_id'), currentPage]);

  const getList = () => {
    const constructId = getValues('construct_id');
    if (!constructId) {
      return;
    }
    const param = `?construct_id=${constructId.value}&page=${currentPage}`;
    getLedgerList(param).then(data => {
      if (data.data === 'Not logged in!') {
        changeLoggedInData('Not logged in!');
      } else {
        if (typeof data.data === 'string') {
          setErrorMessage(data.data);
          setOpen(true);
        } else {
          setLoading(false);
          setLedgerList(data.data);
          setPageCount(data.page_count);
        }
      }
    });
  };

  const onDownload = async (photoLedgerId: number | undefined) => {
    if (!photoLedgerId) {
      return;
    }
    setProgressFlg(true);
    const pdfData = await downloadLedger(photoLedgerId);
    const blob = await LedgerPdf(pdfData, userTenantData);
    if (blob) {
      const currentDatetime = formatDatetime(new Date(), 'YYYYMMDD');
      const fileName = `写真台帳_${currentDatetime}.pdf`;
      const link = document.createElement('a');
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
    }
    setProgressFlg(false);
  };

  const onChangeConstructId = async () => {
    setCurrentPage(1);
    getList();
  };

  if (loggedIn === 'Not logged in!') {
    return <Redirect to='/' />;
  }

  const onCopy = async (photoLedgerId: number) => {
    setProgressFlg(true);
    await copyLedger(photoLedgerId).then(() => {
      setProgressFlg(false);
    });
  };

  const onDelete = async (photoLedgerId: number) => {
    setProgressFlg(true);
    await deleteLedger(photoLedgerId).then(() => {
      setProgressFlg(false);
    });
  };

  return (
    <div style={{ backgroundColor: '#F5F5F5' }}>
      <AppBarComponent patternName='ledger_list' />
      {loading ? (
        <>
          <Snackbar open={open} autoHideDuration={4000} onClose={handleClose}>
            <Alert onClose={handleClose} severity='error'>
              {errorMessage}
            </Alert>
          </Snackbar>
          <LoadingComponent />
        </>
      ) : (
        <div
          style={{
            overflow: 'hidden',
            backgroundColor: '#F5F5F5',
            padding: 15,
            minHeight: window.innerHeight - 150
          }}
        >
          <Snackbar open={open} autoHideDuration={4000} onClose={handleClose}>
            <Alert onClose={handleClose} severity='error'>
              {errorMessage}
            </Alert>
          </Snackbar>

          <div
            style={{ padding: 15, backgroundColor: '#FFFFFF', borderRadius: 5 }}
          >
            <div style={{ backgroundColor: '#FFFFFF' }}>
              <div
                style={{
                  fontWeight: 'bold',
                  padding: 15,
                  fontSize: 24
                }}
              >
                写真台帳一覧
              </div>
            </div>
            <Grid container>
              <FormProvider {...methods}>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    marginBottom: 20,
                    flexWrap: 'wrap',
                    overflow: 'hidden',
                    flexDirection: isWideScreen ? 'row' : 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div style={{ paddingLeft: 5, margin: 5 }}>
                    <AppSelectfield
                      name='construct_id'
                      defaultValue={{ value: 0, label: '案件名' }}
                      listDatas={constructList}
                      onChange={onChangeConstructId}
                      required={true}
                      error={'construct_id' in errors}
                      style={{ width: 300 }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                      style={{
                        backgroundColor:
                          getValues('construct_id').value &&
                          getValues('construct_id').value != 0
                            ? '#52555F'
                            : '#383b45',
                        color:
                          getValues('construct_id').value &&
                          getValues('construct_id').value != 0
                            ? 'white'
                            : '#52555F',
                        margin: 5
                      }}
                      variant='contained'
                      color='secondary'
                      disabled={
                        getValues('construct_id').value &&
                        getValues('construct_id').value != 0
                          ? false
                          : true
                      }
                      onClick={() => {
                        const constructId = getValues('construct_id');
                        if (!constructId || constructId.value === 0) {
                          enqueueSnackbar('案件を選択してください', {
                            variant: 'error'
                          });
                          return;
                        }
                        history.push('/create-ledger', {
                          constructId,
                          ledgerListFlg: true
                        });
                      }}
                    >
                      写真台帳の新規作成
                    </Button>
                  </div>
                </div>
              </FormProvider>
            </Grid>

            <TableContainer component={Paper}>
              <Table className={classes.main} aria-label='customized table'>
                <TableHead>
                  <TableRow>
                    <StyledTableCell
                      style={{ minWidth: 250 }}
                      className={classes.table}
                      align='center'
                    >
                      タイトル
                    </StyledTableCell>
                    <StyledTableCell
                      style={{ minWidth: 80 }}
                      className={classes.table}
                      align='center'
                    >
                      作成日時
                    </StyledTableCell>
                    <StyledTableCell
                      style={{ minWidth: 80 }}
                      className={classes.table}
                      align='center'
                    >
                      作成者
                    </StyledTableCell>
                    <StyledTableCell
                      style={{ minWidth: 80 }}
                      className={classes.table}
                      align='center'
                    >
                      枚数
                    </StyledTableCell>
                    <StyledTableCell
                      style={{ minWidth: 80 }}
                      className={classes.table}
                      align='center'
                    ></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerList &&
                    ledgerList.length > 0 &&
                    ledgerList.map((ledger: TLedger, index: number) => (
                      <List
                        index={index}
                        ledger={ledger}
                        onDownload={onDownload}
                        onCopy={onCopy}
                        getList={getList}
                        onDelete={onDelete}
                      />
                    ))}
                </TableBody>
              </Table>
              {!loading && ledgerList && ledgerList.length === 0 && (
                <div className={classes.container}>データがありません</div>
              )}
            </TableContainer>
            <PaginationComponent
              page={currentPage}
              pageCount={pageCount}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      )}
      <FooterComponent />
    </div>
  );
};

type TStyleProps = {
  mainColor: string;
};

const useStyles = makeStyles<Theme, TStyleProps>((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 15
    },
    main: {
      minWidth: 700
    },
    table: {
      padding: 8,
      backgroundColor: props => `${props.mainColor} !important`,
      color: '#FFFFFF',
      fontWeight: 'bold',
      fontSize: 16
    }
  })
);
